// Created file by Gauri to update Action Item, Resource and Funding Tabs history on details view on 04 Nov 2025
import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Dropdown } from "@fluentui/react";
// Added by Gauri to fix dropdown position issue on 20 Nov 2025
import { DirectionalHint } from "@fluentui/react";
import Table from "react-bootstrap/Table";
import AxiosInstance1 from "app/hooks/AxiosInstance/AxiosInstance1";

const ShowHistoryDrawer = ({ onBack, uniqueID, auditType }) => {
  const [historyData, setHistoryData] = useState([]);
  const [HisCurrentPage, setHisCurrentPage] = useState(1);
  const [selectedField, setSelectedField] = useState(""); // State for Modified Field
  const [selectedBy, setSelectedBy] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState(5);

  // Fetch audit history data
  const fetchAuditHistory = async () => {
    if (!uniqueID) {
      setHistoryData([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      const params = {
        AuditType: auditType,
        UniqueID: uniqueID,
        PageNumber: HisCurrentPage,
        PageSize: pageSize,
      };

      // Add filters if selected
      if (selectedField) {
        params.ModifiedField = selectedField;
      }
      if (selectedBy) {
        params.ModifiedBy = selectedBy;
      }

      console.log('Fetching audit history with params:', params);
      const response = await AxiosInstance1.get('/api/InitiativeDetail/GetInitiativeTabsHistory', { params });

      const auditData = response.data?.data?.auditTrails || [];
      const totalRecords = response.data?.paginationEntities?.[0]?.totalRecords || 0;

      console.log('History API Response:', {
        currentPage: HisCurrentPage,
        pageSize: pageSize,
        dataLength: auditData.length,
        totalRecords: totalRecords,
        response: response.data
      });

      setHistoryData(auditData);
      setTotalCount(totalRecords);
    } catch (error) {
      console.error('Error fetching audit history:', error);
      setHistoryData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all unique values for dropdowns without filters
  const fetchAllUniqueValues = async () => {
    if (!uniqueID) return;

    try {
      const params = {
        AuditType: auditType,
        UniqueID: uniqueID,
        PageNumber: 1,
        PageSize: 1000, // Large page size to get all records
      };

      const response = await AxiosInstance1.get('/api/InitiativeDetail/GetInitiativeTabsHistory', { params });
      const allData = response.data?.data?.auditTrails || [];

      // Extract all unique field names
      const uniqueFields = [...new Set(allData.map(item => item.modifiedField).filter(Boolean))];
      setAllUniqueFields(uniqueFields);

      // Extract all unique modified by names
      const uniqueModifiedBy = [...new Set(allData.map(item => item.modifiedBy).filter(Boolean))];
      setAllUniqueModifiedBy(uniqueModifiedBy);

      // Update dropdown options with all unique values
      setFieldOptions([
        { key: "", text: "Select Modified Field" },
        ...uniqueFields.map(field => ({ key: field, text: field }))
      ]);

      setModifiedByOptions([
        { key: "", text: "Select Modified By" },
        ...uniqueModifiedBy.map(name => ({ key: name, text: name }))
      ]);
    } catch (error) {
      console.error('Error fetching unique values:', error);
    }
  };

  const [fieldOptions, setFieldOptions] = useState([
    { key: "", text: "Select Modified Field" }
  ]);
  const [modifiedByOptions, setModifiedByOptions] = useState([
    { key: "", text: "Select Modified By" }
  ]);
  const [allUniqueFields, setAllUniqueFields] = useState([]);
  const [allUniqueModifiedBy, setAllUniqueModifiedBy] = useState([]);
  // useEffect to fetch data when component mounts or dependencies change
  useEffect(() => {
    if (uniqueID) {
      fetchAuditHistory();
    }
  }, [uniqueID, HisCurrentPage, selectedField, selectedBy]);

  // useEffect to fetch unique values only when uniqueID changes
  useEffect(() => {
    if (uniqueID) {
      fetchAllUniqueValues();
    }
  }, [uniqueID]);

  const handleFieldChange = (event, option) => {
    setSelectedField(option?.key || "");
    setHisCurrentPage(1);
  };

  const handleByChange = (event, option) => {
    setSelectedBy(option?.key || "");
    setHisCurrentPage(1);
  };

  const handlePreviousPage = () => {
    console.log('Previous page clicked, current page:', HisCurrentPage);
    setHisCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    console.log('Next page clicked, current page:', HisCurrentPage, 'totalCount:', totalCount, 'pageSize:', pageSize);
    setHisCurrentPage((prevPage) => prevPage + 1);
  };

  // Format value - if it's a date, format it, if it's a number, format with commas, otherwise return as is
  const formatValue = (value) => {
    if (!value || value === "N/A") return value || "N/A";

    const trimmedValue = String(value).trim();

    // Check if value is a simple number (digits with optional decimal)
    // If it's just digits (with optional decimal), format with commas
    if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
      // Split into integer and decimal parts
      const parts = trimmedValue.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1] || '';

      // Add commas to integer part
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      // Combine integer and decimal parts
      return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    }

    // Month name mapping
    const monthMap = {
      'jan': 1, 'january': 1,
      'feb': 2, 'february': 2,
      'mar': 3, 'march': 3,
      'apr': 4, 'april': 4,
      'may': 5,
      'jun': 6, 'june': 6,
      'jul': 7, 'july': 7,
      'aug': 8, 'august': 8,
      'sep': 9, 'september': 9, 'sept': 9,
      'oct': 10, 'october': 10,
      'nov': 11, 'november': 11,
      'dec': 12, 'december': 12
    };

    // Added by Gauri to handle format like "07 Sep 2024" on 06 Nov 2025
    // Check for format like "DD MMM YYYY" (e.g., "07 Sep 2024")
    const dayMonthYearPattern = /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/i;
    const dayMonthYearMatch = trimmedValue.match(dayMonthYearPattern);

    if (dayMonthYearMatch) {
      try {
        const day = parseInt(dayMonthYearMatch[1], 10);
        const monthName = dayMonthYearMatch[2].toLowerCase();
        const year = parseInt(dayMonthYearMatch[3], 10);
        const month = monthMap[monthName];

        if (month && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
          // Format as DD/MM/YYYY
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
        }
      } catch (e) {
        console.log("Date parsing error for DD MMM YYYY format:", trimmedValue, e);
      }
    }

    // Check for format like "Feb 10 2023 12:00AM" or "Feb 10 2023"
    // This pattern requires a month name (3-9 letters), which indicates it's a date string
    const dateTimePattern = /^([A-Za-z]{3,9})\s+(\d{1,2})\s+(\d{4})(\s+\d{1,2}:\d{2}(:\d{2})?(AM|PM))?$/i;
    const match = trimmedValue.match(dateTimePattern);

    if (match) {
      try {
        const monthName = match[1].toLowerCase();
        const day = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);
        const month = monthMap[monthName];

        if (month && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
          // Format as DD/MM/YYYY
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
        }
      } catch (e) {
        console.log("Date parsing error for pattern match:", trimmedValue, e);
      }
    }

    // Check for ISO format "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS"
    // Only format if it has the full date structure with dashes
    const isoPattern = /^(\d{4})-(\d{2})-(\d{2})(\s+\d{2}:\d{2}:\d{2})?$/;
    const isoMatch = trimmedValue.match(isoPattern);

    if (isoMatch) {
      try {
        const year = parseInt(isoMatch[1], 10);
        const month = parseInt(isoMatch[2], 10);
        const day = parseInt(isoMatch[3], 10);

        if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          // Format as DD/MM/YYYY
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
        }
      } catch (e) {
        console.log("Date parsing error for ISO format:", trimmedValue, e);
      }
    }

    // Check for format like "MM/DD/YYYY" or "DD/MM/YYYY"
    // Only format if it has slashes indicating it's already a date format
    const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const slashMatch = trimmedValue.match(slashPattern);

    if (slashMatch) {
      try {
        const part1 = parseInt(slashMatch[1], 10);
        const part2 = parseInt(slashMatch[2], 10);
        const year = parseInt(slashMatch[3], 10);

        // Determine if it's MM/DD/YYYY or DD/MM/YYYY by checking which part is <= 12
        let day, month;
        if (part1 <= 12 && part2 > 12) {
          // Likely MM/DD/YYYY
          month = part1;
          day = part2;
        } else if (part2 <= 12 && part1 > 12) {
          // Likely DD/MM/YYYY
          day = part1;
          month = part2;
        } else {
          // Ambiguous, assume DD/MM/YYYY
          day = part1;
          month = part2;
        }

        if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          // Format as DD/MM/YYYY
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
        }
      } catch (e) {
        console.log("Date parsing error for slash format:", trimmedValue, e);
      }
    }

    // Don't use Date constructor as fallback because it can parse numbers incorrectly
    // Only format if we have a clear date pattern match above

    return trimmedValue;
  };

  // Added by Gauri on 20 Nov 2025 - Common calloutProps for all dropdowns
  const commonCalloutProps = {
    // Use bottomAutoEdge which tries to open downward first, then automatically adjusts upward if needed
    directionalHint: DirectionalHint.bottomAutoEdge,
    // Set max height to prevent dropdown from being too large
    calloutMaxHeight: 250,
    // Ensure dropdown stays within viewport bounds
    bounds: typeof window !== 'undefined' ? {
      top: 0,
      left: 0,
      bottom: window.innerHeight,
      right: window.innerWidth
    } : undefined
  };

  return (
    <div>
      <Grid container spacing={2} alignItems="center" className="my-2">
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ fontSize: "0.775rem" }}>Modified Field</Typography>
          {/* Modified by Gauri to fix dropdown position issue on 20 Nov 2025 */}
          <Dropdown
            placeholder="Select Modified Field"
            options={fieldOptions}
            selectedKey={selectedField || ""}
            onChange={handleFieldChange}
            styles={{ dropdown: { width: 200 } }}
            calloutProps={commonCalloutProps}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ fontSize: "0.775rem" }}>Modified By</Typography>
          {/* Modified by Gauri to fix dropdown position issue on 20 Nov 2025 */}
          <Dropdown
            placeholder="Select Modified By"
            options={modifiedByOptions}
            selectedKey={selectedBy || ""}
            onChange={handleByChange}
            styles={{ dropdown: { width: 200 } }}
            calloutProps={commonCalloutProps}
          />
        </Grid>
      </Grid>

      {/* Table format changed by Gauri Start Here */}
      <Table size="small" bordered hover responsive>
        <thead>
          <tr>
            <th>Modified Field</th>
            <th>Modified Date</th>
            <th>Old Value</th>
            <th>New Value</th>
            <th>Modified By</th>
          </tr>
        </thead>
        <tbody>
          {historyData.length > 0 ? (
            historyData.map((row, index) => (
              <tr key={index}>
                <td>{row.modifiedField || "N/A"}</td>
                <td>
                  {row.modifiedDate
                    ? new Date(row.modifiedDate).toLocaleDateString("en-GB")
                    : "N/A"}
                </td>
                <td>{formatValue(row.oldValue)}</td>
                <td>{formatValue(row.newValue)}</td>
                <td>{row.modifiedBy || "N/A"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" style={{ textAlign: "center" }}>
                There are no items to show in this view.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination controls */}
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <IconButton onClick={handlePreviousPage} disabled={HisCurrentPage === 1}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ margin: "0 10px", alignSelf: "center" }}>
          Page {HisCurrentPage}
        </Typography>
        <IconButton
          onClick={handleNextPage}
          disabled={HisCurrentPage >= Math.ceil(totalCount / pageSize)}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>
    </div>
  );
};

export default ShowHistoryDrawer;